import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Globe } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { formatApiErrorDetail } from "../lib/api";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const { content, lang, toggleLang, t } = useLang();
  const l = content.login || {};
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [expired, setExpired] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setExpired(false);
    try {
      const user = await login(form.email, form.password);
      toast.success(t("Welcome back!", "Bon retour !"));
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (typeof detail === "string" && detail.includes("MEMBERSHIP_EXPIRED")) {
        setExpired(true);
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const renew = async () => {
    setRenewing(true);
    try {
      const { data } = await api.post("/payments/renew", {
        email: form.email,
        password: form.password,
        origin_url: window.location.origin,
        billing_cycle: "monthly",
      });
      if (data.free) {
        toast.success(t("Your membership is active again — please sign in.", "Votre adhésion est réactivée — veuillez vous connecter."));
        setExpired(false);
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setRenewing(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src="https://images.pexels.com/photos/7491158/pexels-photo-7491158.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=900" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-12 left-12 text-primary-foreground">
          <p className="text-xs uppercase tracking-[0.3em] text-accent font-semibold">Welcome back</p>
          <h2 className="font-display text-5xl font-medium mt-2 max-w-sm">{l.hero_body}</h2>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 md:px-16 py-12 bg-background relative">
        <div className="absolute top-6 right-6 flex items-center gap-4">
          <button onClick={toggleLang} className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-secondary"><Globe size={16} /> {lang === "en" ? "FR" : "EN"}</button>
          <Link to="/" className="font-display text-2xl font-semibold text-primary">GYBC</Link>
        </div>
        <div className="max-w-md w-full mx-auto">
          <h1 className="font-display text-4xl md:text-5xl font-medium text-primary">{l.form_title}</h1>
          <p className="text-foreground/70 mt-3">{l.form_body}</p>
          {expired && (
            <div data-testid="login-expired-banner" className="mt-6 rounded-2xl border border-secondary/30 bg-secondary/5 p-5">
              <p className="font-display text-xl font-semibold text-primary">{t("Your membership has expired", "Votre adhésion a expiré")}</p>
              <p className="text-sm text-foreground/70 mt-1">{t("Renew now to regain access to your dashboard and member benefits.", "Renouvelez maintenant pour retrouver l'accès à votre tableau de bord et à vos avantages.")}</p>
              <button type="button" onClick={renew} disabled={renewing} data-testid="login-renew-btn" className="mt-4 rounded-full bg-secondary text-secondary-foreground px-6 py-3 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
                {renewing ? t("Opening secure checkout…", "Ouverture du paiement sécurisé…") : t("Renew membership", "Renouveler l'adhésion")}
              </button>
            </div>
          )}
          <form onSubmit={submit} className="space-y-5 mt-8" data-testid="login-form">
            <div>
              <Label htmlFor="email">{t("Email", "Courriel")}</Label>
              <Input id="email" type="email" data-testid="login-email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="password">{t("Password", "Mot de passe")}</Label>
              <Input id="password" type="password" data-testid="login-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 rounded-xl" />
              <div className="text-right mt-2">
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-sm font-semibold text-secondary hover:underline">{t("Forgot password?", "Mot de passe oublié ?")}</Link>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full bg-primary text-primary-foreground py-3.5 font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
              {loading ? t("Signing in...", "Connexion...") : t("Sign in", "Se connecter")}
            </button>
          </form>
          <p className="text-sm text-foreground/60 mt-6">{t("New to GYBC?", "Nouvelle chez GYBC ?")} <Link to="/join" className="text-secondary font-semibold hover:underline">{t("Join the club.", "Rejoignez le club.")}</Link></p>
        </div>
      </div>
    </div>
  );
}
